import React from 'react'
import PropTypes from 'prop-types'
import { Title, formatSalary } from '@offerzen/pinata'

import DescriptionList, { DescriptionItem } from '@/Jobs/components/DescriptionList'
import RequirementTag from '@/Jobs/components/RequirementTag'
import { workplacePolicyTypeMap, employmentTypeMap, remunerationPeriodMap, authStatusMap } from '@/Jobs/helpers/constants'

import VisaRequirementDetail from './VisaRequirementDetail'
import styles from './styles'

const LocationAndSalarySection = ({
  workplacePolicy,
  otherLocations,
  requiresWorkAuthorisation,
  workAuthorisation,
  visaSponsorshipAvailable,
  employmentType,
  contractDuration,
  publishedSalaryRange,
  currency,
  remunerationPeriod,
}) => {
  return (
    <section>
      <Title className={styles.heading}>Location and salary</Title>

      <DescriptionList>
        <DescriptionItem
          term="Location of position"
          details={
            <>
              <If condition={workplacePolicy?.type}>{workplacePolicyTypeMap[workplacePolicy.type]} from</If>
              <Choose>
                <When condition={workplacePolicy?.type === 'REMOTE' && !workplacePolicy?.comprehensiveLocations?.length}>
                  <ul>
                    <li>
                      <RequirementTag text="Anywhere" />
                    </li>
                  </ul>
                </When>
                <Otherwise>
                  <If condition={workplacePolicy?.comprehensiveLocations?.length}>
                    <ul>
                      <For each="workLocation" of={workplacePolicy.comprehensiveLocations}>
                        <li key={workLocation.displayAddress}>
                          <RequirementTag text={workLocation.displayAddress} />
                        </li>
                      </For>
                    </ul>
                  </If>
                </Otherwise>
              </Choose>
            </>
          }
        />

        <If condition={otherLocations?.length}>
          <DescriptionItem
            term="We'll also consider candidates in"
            details={
              <ul>
                <For each="otherLocation" of={otherLocations}>
                  <li key={otherLocation.displayAddress}>
                    <RequirementTag text={otherLocation.displayAddress} />
                  </li>
                </For>
              </ul>
            }
          />
        </If>

        <DescriptionItem
          term="Visa requirements"
          details={
            <Choose>
              <When condition={[null, undefined].includes(requiresWorkAuthorisation)} />
              <When condition={requiresWorkAuthorisation === false}>No specific requirements</When>

              <When condition={!workAuthorisation?.countries?.length || !workAuthorisation?.statuses?.length} />
              <Otherwise>
                <div className={styles.visaRequirements}>
                  <VisaRequirementDetail
                    label="Authorised to work in"
                    requirements={workAuthorisation?.countries?.map(country => country.name)}
                  />
                  <VisaRequirementDetail
                    label="With status of"
                    requirements={workAuthorisation?.statuses?.map(status => authStatusMap[status])}
                  />
                </div>
              </Otherwise>
            </Choose>
          }
        />

        <DescriptionItem
          term="Visa sponsorship"
          details={
            <Choose>
              <When condition={visaSponsorshipAvailable === true}>Able to sponsor visa</When>
              <When condition={visaSponsorshipAvailable === false}>Unable to sponsor visa</When>
              <Otherwise>Not disclosed</Otherwise>
            </Choose>
          }
        />

        <DescriptionItem
          term="Employment type"
          details={
            <Choose>
              <When condition={!employmentType} />
              <When condition={employmentType === 'CONTRACT' && !contractDuration} />
              <When condition={employmentType === 'CONTRACT'}>
                {employmentTypeMap[employmentType]}
                {contractDuration && employmentType === 'CONTRACT' ? `, ${contractDuration} months` : ''}
              </When>
              <Otherwise>{employmentTypeMap[employmentType]}</Otherwise>
            </Choose>
          }
        />

        <If condition={publishedSalaryRange && currency?.code}>
          <DescriptionItem
            term="Salary range"
            details={
              <>
                <Choose>
                  <When condition={publishedSalaryRange.start && publishedSalaryRange.end}>
                    {formatSalary(currency.code, publishedSalaryRange.start)} - {formatSalary(currency.code, publishedSalaryRange.end)}
                  </When>
                  <When condition={publishedSalaryRange.start}>From {formatSalary(currency.code, publishedSalaryRange.start)}</When>
                  <When condition={publishedSalaryRange.end}>Up to {formatSalary(currency.code, publishedSalaryRange.end)}</When>
                </Choose>{' '}
                {remunerationPeriodMap[remunerationPeriod]}
              </>
            }
          />
        </If>
      </DescriptionList>
    </section>
  )
}

LocationAndSalarySection.propTypes = {
  workplacePolicy: PropTypes.shape({
    type: PropTypes.oneOf(['HYBRID', 'REMOTE', 'OFFICE']),
    comprehensiveLocations: PropTypes.arrayOf(
      PropTypes.shape({
        displayAddress: PropTypes.string,
      }),
    ),
  }),
  otherLocations: PropTypes.arrayOf(
    PropTypes.shape({
      displayAddress: PropTypes.string,
    }),
  ),
  requiresWorkAuthorisation: PropTypes.bool,
  workAuthorisation: PropTypes.shape({
    countries: PropTypes.arrayOf(
      PropTypes.shape({
        iso2Code: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    statuses: PropTypes.arrayOf(PropTypes.oneOf(['CITIZEN', 'PERMANENT_RESIDENT', 'WORK_PERMIT'])),
  }),
  visaSponsorshipAvailable: PropTypes.bool,
  employmentType: PropTypes.oneOf(['CONTRACT', 'PERMANENT']),
  contractDuration: PropTypes.number,
  publishedSalaryRange: PropTypes.shape({
    start: PropTypes.number,
    end: PropTypes.number,
  }),
  currency: PropTypes.shape({
    code: PropTypes.string,
  }),
  remunerationPeriod: PropTypes.oneOf(['MONTH', 'YEAR']),
}

export default LocationAndSalarySection
